import calculateGetItBy from '@carvana/merch-tile/dist/utils/availability/calculateGetItBy';
import calculateSoonestAvailable from '@carvana/merch-tile/dist/utils/availability/calculateSoonestAvailable';
import cx from 'classnames';
import { useContext } from 'react';
import Skeleton from '@/vdp/HeroVehicleInfo/Skeleton';
import SoftPullStatus from '~/constants/softPullStatus';
import Sections from '~/constants/vdp/sections';
import TransitChargeState from '~/constants/vdp/TransitChargeState';
import DeliveryPickupContext, {
  useDeliveryInfo,
  usePickupInfo
} from '~/context/vdp/DeliveryPickupContext';
import UserVehicleContext, {
  useVdpPaymentTerms,
  useVdpPricingTradeInfo
} from '~/context/vdp/UserVehicleContext';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useShouldDisplayOrderStatus from '~/hooks/vdp/selectors/useShouldDisplayOrderStatus';
import useShouldShowPriceDetails from '~/hooks/vdp/selectors/useShouldShowPriceDetails';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import useTransitChargeInfo from '~/hooks/vdp/selectors/useTransitChargeInfo';
import useIsInSptOnVdpExperiment from '~/hooks/vdp/useIsInSptOnVdpExperiment';
import InfoIcon from '~/icons/srp/InfoIcon';
import { PricingError } from '~/services/srp/contracts/PricingResponse';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';

type OwnProps = {
  canShowTerms: boolean;
  isDeliveryPickupLoading: boolean;
  isTransitChargeLoading: boolean;
  getItByData: {
    hasGetItBy: boolean;
    getItBy: string;
    soonestDays: number;
  };
};

function TermsShippingAndGetItBy({
  canShowTerms,
  isDeliveryPickupLoading,
  isTransitChargeLoading,
  getItByData: { hasGetItBy, getItBy, soonestDays }
}: OwnProps) {
  const transitCharge = useTransitChargeInfo();
  const { softPullStatus } = useSoftPullStatus();
  const hasSoftPull = softPullStatus === SoftPullStatus.Active;
  const { applyTradeIn = false } = useVdpPricingTradeInfo();
  const { monthlyPayment = 0, downPaymentDetails } = useVdpPaymentTerms();
  const { additionalCashDown = 0 } = downPaymentDetails ?? {};
  const {
    vdpPricingError,
    vdpPricingInfo: { isLoaded: isVdpPricingLoaded }
  } = useContext(UserVehicleContext);

  function Shipping() {
    return isTransitChargeLoading ? (
      <Skeleton width={90} />
    ) : (
      <p
        className={cx('-:text-grey-5', {
          'font-bold text-green-2':
            transitCharge.state !== TransitChargeState.HasCharge,
          hidden: transitCharge.state === TransitChargeState.Unknown
        })}
      >
        {transitCharge.state === TransitChargeState.HasCharge
          ? `$${formatAndDropDecimal(transitCharge.charge)}`
          : 'Free'}{' '}
        shipping
      </p>
    );
  }

  function Terms() {
    if (!isVdpPricingLoaded) {
      return (
        <div className="flex flex-col">
          <Skeleton width={200} />
          <div className="flex items-center gap-4">
            <Skeleton width={105} />
            <div className="h-[3px] w-[3px] rounded-full bg-grey-3" />
            <Skeleton width={105} />
          </div>
        </div>
      );
    }

    if (vdpPricingError !== PricingError.None) {
      return hasSoftPull ? (
        <div className="text-grey-5 tl-body-m">
          <p>Unable to load payment terms</p>
          <Shipping />
        </div>
      ) : (
        <Shipping />
      );
    }

    return (
      <div className="text-grey-5 tl-body-m">
        <p className="flex items-center gap-4">
          {hasSoftPull ? '' : 'Estimated'}{' '}
          <span className={hasSoftPull ? 'text-blue-6 tl-header-s' : ''}>
            ${monthlyPayment.toLocaleString()}/mo
          </span>{' '}
          {applyTradeIn ? 'w/ trade-in' : ''}
          <a
            data-analytics-id="Hero Price-Details Link"
            href={`#${Sections.PriceDetails}`}
          >
            <InfoIcon />
          </a>
        </p>
        <div className="flex items-center gap-8">
          <p
            className={
              additionalCashDown === 0 && hasSoftPull
                ? 'text-green-2 tl-header-xs'
                : ''
            }
          >
            ${formatAndDropDecimal(additionalCashDown)} cash down
          </p>
          {transitCharge.state !== TransitChargeState.Unknown && (
            <div className="h-[3px] w-[3px] rounded-full bg-grey-3" />
          )}
          <Shipping />
        </div>
      </div>
    );
  }

  return (
    <>
      {canShowTerms ? <Terms /> : <Shipping />}

      {isDeliveryPickupLoading ? (
        <Skeleton width={105} />
      ) : (
        <p
          className={cx('text-blue-6', {
            'font-bold': soonestDays < 4,
            hidden: !hasGetItBy
          })}
        >
          Get it {getItBy}
        </p>
      )}
    </>
  );
}

export default function Wrapper() {
  const isInSptOnVdpExperiment = useIsInSptOnVdpExperiment();
  const shouldShowPriceDetails = useShouldShowPriceDetails();
  const shouldDisplayOrderStatus = useShouldDisplayOrderStatus();

  const canShowTerms =
    isInSptOnVdpExperiment &&
    shouldShowPriceDetails &&
    !shouldDisplayOrderStatus;

  const { deliveryPickup } = useContext(DeliveryPickupContext);
  const delivery = useDeliveryInfo();
  const pickup = usePickupInfo();
  const transitCharge = useTransitChargeInfo();
  const soonestAvailable = calculateSoonestAvailable({ delivery, pickup });
  const { getItBy } = calculateGetItBy(soonestAvailable, false, true);

  const isAvailableForImmediatePurchase =
    !useIsNotAvailableForImmediatePurchase();
  const isDeliveryPickupLoading =
    isAvailableForImmediatePurchase && !deliveryPickup.isLoaded;
  const isTransitChargeLoading =
    isAvailableForImmediatePurchase && !transitCharge.isLoaded;

  const hasTransitCharge =
    transitCharge.state !== TransitChargeState.Unknown ||
    isTransitChargeLoading;
  const hasGetItBy = Boolean(getItBy) || isDeliveryPickupLoading;

  const shouldLoadTermsShippingGetItBy =
    canShowTerms || hasTransitCharge || hasGetItBy;

  return (
    shouldLoadTermsShippingGetItBy && (
      <div className="flex flex-col gap-y-md m:tl-body-m">
        <TermsShippingAndGetItBy
          canShowTerms={canShowTerms}
          isTransitChargeLoading={isTransitChargeLoading}
          isDeliveryPickupLoading={isDeliveryPickupLoading}
          getItByData={{
            hasGetItBy,
            getItBy,
            soonestDays: Number(soonestAvailable.daysToAvailability)
          }}
        />
      </div>
    )
  );
}
